<script setup lang="ts">
import { KopItem } from '@/models/kop/kopItem'
import { $approveKopItem, $getKopItemWfPreviousStatuses } from '@/api/kop'
import { useDrawingStore } from '@/store/drawing'
import { useSnackbarStore } from '@/store/snackbar'

interface IProps {
  kopItem?: KopItem,
}

const props = withDefaults(defineProps<IProps>(), {
  kopItem: () => ({}),
})

interface Emit {
  (e: 'getKopItem'): void
}

const emit = defineEmits<Emit>()

const drawingStore = useDrawingStore()
const snackbarStore = useSnackbarStore()

const approveBtnLoading = ref(false)
const rejectBtnLoading = ref(false)

const previousStatusList = ref([])
const previousStatusListLoading = ref(false)

const approveKopItem = async (isApprove = true, rejectStatusId = null) => {
  if (!props.kopItem?.id) return

  if (isApprove) {
    approveBtnLoading.value = true
  } else {
    rejectBtnLoading.value = true
  }
  try {
    await $approveKopItem(props.kopItem?.id, {
      is_approve: isApprove,
      reject_status_id: rejectStatusId ? rejectStatusId : undefined
    })
    snackbarStore.showMessage({
      color: 'success',
      icon: 'mdi-checkbox-marked-circle',
      title: 'Success',
      text: 'KOP Item updated successfully!'
    })
    emit('getKopItem')
  } catch (e: any) {
    const mes = e.response.data?.message ? e.response.data?.message : e.response.data
    snackbarStore.showMessage({
      color: 'error',
      icon: 'mdi-alert-circle',
      title: 'Error',
      text: `Oops, something went wrong. ${mes}`
    })
  } finally {
    if (isApprove) {
      approveBtnLoading.value = false
    } else {
      rejectBtnLoading.value = false
    }
  }
}

const rejectKopItem = async (rejectStatusId = null) => {
  await approveKopItem(false, rejectStatusId)
}

const getKopItemWfPreviousStatuses = async () => {
  if (!props.kopItem?.id) return

  previousStatusListLoading.value = true
  try {
    const res = await $getKopItemWfPreviousStatuses(props.kopItem?.id)
    previousStatusList.value = res.data
  } catch (e: any) {
    const mes = e.response.data ? e.response.data : e.message
    snackbarStore.showMessage({
      color: 'error',
      icon: 'mdi-alert-circle',
      title: 'Error',
      text: `Oops, something went wrong. ${mes}`
    })
  } finally {
    previousStatusListLoading.value = false
  }
}

const handleRejectMenuOpen = (isOpen: boolean) => {
  if (isOpen) {
    getKopItemWfPreviousStatuses()
  }
}
</script>

<template>
  <div class="d-flex align-center flex-wrap">
    <template v-if="kopItem?.is_can_user_approve">
      <VBtn
        class="mr-2 mt-1 mt-sm-0"
        :disabled="!!approveBtnLoading"
        color="success"
        size="small"
        @click="approveKopItem"
      >
        <VIcon
          icon="tabler-check"
        />
        Approve
        <VProgressCircular
          v-if="!!approveBtnLoading"
          indeterminate
          size="16"
          width="2"
          class="ml-1"
          color="light"
        />
      </VBtn>

      <VBtn
        v-if="kopItem?.is_released"
        :disabled="!!rejectBtnLoading"
        size="small"
        class="mt-1 mt-sm-0"
        @click="rejectKopItem(null)"
      >
        <VIcon
          icon="tabler-x"
        />
        Reject
        <VProgressCircular
          v-if="!!rejectBtnLoading"
          indeterminate
          size="16"
          width="2"
          class="ml-1"
          color="light"
        />
      </VBtn>
      <VMenu
        v-else
        location="bottom center"
        open-on-click
        @update:modelValue="handleRejectMenuOpen"
      >
        <template #activator="{ props }">
          <VBtn
            :disabled="!!rejectBtnLoading"
            size="small"
            class="mt-1 mt-sm-0"
            v-bind="props"
          >
            <VIcon
              icon="tabler-x"
            />
            Reject
            <VProgressCircular
              v-if="!!rejectBtnLoading"
              indeterminate
              size="16"
              width="2"
              class="ml-1"
              color="light"
            />
          </VBtn>
        </template>
        <VCard class="position-relative reject-previous-statuses-menu-block">
          <VOverlay
            v-model="previousStatusListLoading"
            persistent
            absolute
            contained
            class="align-center justify-center"
          >
            <VProgressCircular
              indeterminate
              size="16"
              width="2"
              color="primary"
            />
          </VOverlay>
          <div
            v-if="!previousStatusList.length && !previousStatusListLoading"
            class="px-2 py-3"
          >
            Previous statuses not found
          </div>
          <VList
            v-else
            class="px-1"
            :class="{'pa-5': previousStatusListLoading}"
            nav
          >
            <div
              v-if="previousStatusList.length"
              class="text-body-2 text-center no-pointer-events"
            >
              Back to:
            </div>
            <template
              v-for="(status, statusKey) in previousStatusList"
              :key="statusKey"
            >
              <VListItem
                :title="status?.name"
                class="border-t rounded-t-0"
                @click="rejectKopItem(status?.id)"
              />
            </template>
          </VList>
        </VCard>
      </VMenu>
    </template>
    <VAlert
      v-else-if="kopItem?.is_on_hold"
      show
      variant="tonal"
      color="warning"
      class="text-body-2 py-1"
    >
      KOP Items flagged as "On Hold"<br>cannot be Approved/Rejected
    </VAlert>
  </div>
</template>

<style lang="scss">
.reject-previous-statuses-menu-block {
  box-shadow: 0 3px 17px -10px #5e5e5e !important;
}
</style>
